import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, User, LogOut, LayoutDashboard, ArrowUpRight } from 'lucide-react';
import { menuData, navItems } from '../data/menuData.js';
import logo from '../assets/logo.png';
import logowebp from '../assets/logob.webp';
import { useAuth } from '../context/AuthContext';

export function MegaMenu({ background }) {
  const [activeMenu, setActiveMenu] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const navigate = useNavigate();
  const { logout: authLogout } = useAuth();

  const megaMenuRef = useRef(null);
  const userMenuRef = useRef(null);

  // Check for token on component mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
  }, []);

  const handleMenuClick = (index) => {
    setActiveMenu(index);
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setActiveMenu(null);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const getNavItemHref = (item) => {
    switch (item) {
      case 'Educaional Tours':
        return '/destination';
      case 'About Us':
        return '/aboutus';
      case 'Study Abroad':
        return '/programs';
      default:
        return '#';
    }
  };
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Handle click outside the Mega Menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (megaMenuRef.current && !megaMenuRef.current.contains(event.target)) {
        handleClose();  // Close the Mega Menu if the click is outside
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Add click outside handler for user menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    authLogout(); // Use the context's logout function
    setIsLoggedIn(false);
    setShowUserMenu(false);
    navigate('/');
  };

  return (
    <nav className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${isScrolled ? "bg-white shadow-md" : `${background}`
      }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            <div className="flex items-center space-x-2">
              <img
                src={`${isScrolled ? logowebp : logo}`}
                alt="Applicationuni Logo"
                className="h-6 lg:h-6 md:h-8"
              />
              <Link to="/">
                <h1
                  className={`${isScrolled ? "text-blue-800" : "text-white"
                    } text-sm md:text-md lg:text-lg 2xl:text-lg font-bold`}
                  onClick={scrollToTop}
                >
                  ApplicationUni
                </h1>
              </Link>
            </div>

          </div>
          <div className="-mr-2 flex items-center md:hidden">
            <button
              onClick={toggleMobileMenu}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 "
            >
              <span className="sr-only ">Open main menu</span>
              {isMobileMenuOpen ? (
                <X className="block h-6 w-6" aria-hidden="true" />
              ) : (
                <Menu className="block h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>
          <div className={`hidden md:flex space-x-3 lg:space-x-6 pt-6 font-bold text-sm md:text-md 2xl:text-xl 2xl:pt-4 ${isScrolled ? "text-blue-600" : "text-white"
            }`}>
            <div className="hidden md:ml-6 md:flex md:space-x-8 ">
              {navItems.map((item, index) => (
                <Link
                  key={item}
                  href={getNavItemHref(item)}
                  className={`hidden hover:underline-offset-4 -mt-6 md:flex space-x-6 pt-6 font-bold text-sm md:text-md 2xl:text-lg ${isScrolled ? "text-blue-600" : "text-white"
                    }`}
                  onClick={() => index === 0 && handleMenuClick(0)}
                >
                  {item}
                </Link>
              ))}
            </div>
            <Link to="/destination" className="hover:underline hover:underline-offset-8 " onClick={scrollToTop}>
              Educational Tours
            </Link>
            <Link to="/programs" className="hover:underline hover:underline-offset-8" onClick={scrollToTop}>
              Study Abroad
            </Link>
            <Link to="/corporate-travel" className="hover:underline hover:underline-offset-8" onClick={scrollToTop}>
              Corporate Travel
            </Link>
            <Link to="/aboutus" className="hover:underline hover:underline-offset-8" onClick={scrollToTop}>
              About
            </Link>

            <Link to="/contact" className="hover:underline hover:underline-offset-8" onClick={scrollToTop}>
              Contact
            </Link>
            <Link
              to="/eligibility"
              className="hover:underline hover:underline-offset-8 flex gap-1"
              onClick={scrollToTop}
            >
              Check Eligibility
              <ArrowUpRight className="h-4 w-4 mt-1" />
            </Link>

            {/* Add Login/User Menu Button */}
            {!isLoggedIn ? (
              <Link
                to="/login"
                className={`
                  flex items-center gap-2 px-4 py-2 rounded-lg
                  font-medium transition-all duration-300 mb-3 -mt-3 2xl:-mt-1 2xl:mb-2
                  ${isScrolled
                    ? "bg-blue-600 text-white hover:bg-blue-700"
                    : "bg-white text-blue-600 hover:bg-blue-200"
                  }`}
                onClick={() => window.scrollTo(0, 0)}
              >
                Login
              </Link>
            ) : (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className={`
                    flex items-center gap-2 px-4 py-2 rounded-lg
                    font-medium transition-all duration-300 mb-3 -mt-3 2xl:-mt-1 2xl:mb-2
                    ${isScrolled
                      ? "bg-blue-600 text-white hover:bg-blue-700"
                      : "bg-white text-blue-600 hover:bg-blue-200"
                    }`}
                >
                  <User className="h-[22px] w-[22px]" />
                </button>
                
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2 z-50">
                    <Link
                      to="/dashboard"
                      className="flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <LayoutDashboard className="h-5 w-5 mr-2" />
                      Dashboard
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2 text-gray-700 hover:bg-gray-100"
                    >
                      <LogOut className="h-5 w-5 mr-2" />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`${isMobileMenuOpen ? 'block' : 'hidden'
          } md:hidden bg-white fixed top-16 left-0 w-full h-[calc(100vh-4rem)] overflow-y-auto`}
      >
        <div className="pt-2 pb-3 flex space-y-1">
          {navItems.map((item, index) => (
            <Link
              key={item}
              href={getNavItemHref(item)}
              className={`block pl-4 pr-4 pt-2 -mb-4 md:border-l-4 text-base ${index === 0
                ? 'text-center'
                : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                }`}
              onClick={() => {
                if (index === 0) {
                  handleMenuClick(0);
                }
                toggleMobileMenu();
              }}
            >
              {item}
            </Link>
          ))}
        </div>
        <ul className="flex flex-col mx-4 space-y-4 py-4 bg-white">
          <li>
            <Link
              to="/destination"
              className="hover:underline hover:underline-offset-8"
              onClick={() => {
                toggleMenu();
                scrollToTop();
              }}
            >
              Educational Tours
            </Link>
          </li>
          <li>
            <Link
              to="/programs"
              className="hover:underline hover:underline-offset-8"
              onClick={() => {
                toggleMenu();
                scrollToTop();
              }}
            >
              Study Abroad
            </Link>
          </li>
          <li>
            <Link
              to="/corporate-travel"
              className="hover:underline hover:underline-offset-8"
              onClick={() => {
                toggleMenu();
                scrollToTop();
              }}
            >
              Corporate Travel
            </Link>
          </li>
          <li>
            <Link
              to="/ai-certification"
              className="hover:underline hover:underline-offset-8"
              onClick={() => {
                toggleMenu();
                scrollToTop();
              }}
            >
              AI Online Course
            </Link>
          </li>
          <li>
            <Link
              to="/aboutus"
              className="hover:underline hover:underline-offset-8"
              onClick={() => {
                toggleMenu();
                scrollToTop();
              }}
            >
              About
            </Link>
          </li>
          <li>
            <Link
              to="/contact"
              className="hover:underline hover:underline-offset-8"
              onClick={() => {
                toggleMenu();
                scrollToTop();
              }}
            >
              Contact
            </Link>
          </li>
          <li>
            <Link
              to="/eligibility" 
              className="hover:underline hover:underline-offset-8 flex items-center gap-1" 
              onClick={scrollToTop}
            >
              Check Eligibility
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </li>
          <li>
            {!isLoggedIn ? (
              <Link
                to="/login"
                className="hover:underline hover:underline-offset-8 flex justify-center gap-2 bg-blue-600 rounded-lg py-2 px-4 text-white"
                onClick={() => {
                  toggleMenu();
                  scrollToTop();
                }}
              >
                Login
              </Link>
            ) : (
              <div className="space-y-2">
                <Link
                  to="/dashboard"
                  className="hover:underline hover:underline-offset-8 flex gap-2 bg-gray-800 rounded-lg py-2 px-2 text-white"
                  onClick={() => {
                    toggleMenu();
                    scrollToTop();
                  }}
                >
                  <LayoutDashboard className="h-5 w-5" />
                  Dashboard
                </Link>
                <button
                  onClick={handleLogout}
                  className="hover:underline hover:underline-offset-8 flex gap-2 bg-gray-800 rounded-lg py-2 px-2 text-white w-full"
                >
                  <LogOut className="h-5 w-5" />
                  Logout
                </button>
              </div>
            )}
          </li>
        </ul>
      </div>

      {/* Mega Menu Panel */}
      {isOpen && (
        <div
          ref={megaMenuRef}
          className="absolute top-full inset-x-0 transform shadow-lg bg-blue-700 text-white max-h-[calc(100vh-4rem)] overflow-y-auto "
        >
          <div className="max-w-7xl mx-auto">
            <div className="grid gap-y-6 px-4 py-6">
              <div>
                <h2 className="text-2xl md:text-4xl font-serif mb-4 " >Browse Programs</h2>
                <p className=" max-w-3xl text-sm md:text-base">
                  Our programs inspire learners in grade 6-12, university, and beyond to leap into an exciting educational journey across a variety of destinations and interests.
                </p>
              </div>

              <div className="grid md:grid-cols-4 gap-8">
                {/* Left Sidebar */}
                <div className="space-y-4">
                  {menuData.map((section, index) => (
                    <div
                      key={section.title}
                      className={`${index === activeMenu
                        ? 'bg-blue-50 text-blue-600 font-medium p-4 rounded'
                        : 'text-white  cursor-pointer'
                        }`}
                      onClick={() => setActiveMenu(index)}
                    >
                      {section.title}
                    </div>
                  ))}
                  <div class="border-t border-gray-300 w-full my-4"></div>

                </div>


                {/* Main Content Area */}
                <div className="md:col-span-3 grid md:grid-cols-2 gap-x-8 gap-y-4">
                  {menuData[activeMenu]?.items.map((item) => (
                    <div key={item.title} className="space-y-4">
                      {/* <h3 className="font-medium text-white">{item.title}</h3> */}
                      {item.subitems && (
                        <ul className="space-y-2">
                          {item.subitems.map((subItem) => (
                            <li key={subItem}>
                              <Link to={`/tours/${subItem.toLowerCase().replace(/ /g, '-')}-tour`} className="text-white hover:text-blue-600" onClick={scrollToTop}>
                                {subItem}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-white hover:text-black"
          >
            Close
          </button>
        </div>
      )}
    </nav>
  );
}
